import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  LocalShipping as ShippingIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { POSProvider } from '../../contexts/POSContext';
import POSLayout from '../../layouts/POSLayout';
import { Order, OrderStatus, OrderType, PaymentStatus } from '../../models/posTypes';
import { usePOS } from '../../contexts/POSContext';
import { format } from 'date-fns';

const OrderHistoryContent: React.FC = () => {
  const theme = useTheme();
  const { orders, loadOrders } = usePOS();
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  const handleStatusFilterChange = (event: React.SyntheticEvent, newValue: OrderStatus | 'all') => {
    setStatusFilter(newValue);
    setPage(0);
  };
  
  const handleViewOrderDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsOrderDetailsOpen(true);
  };
  
  const handleCloseOrderDetails = () => {
    setIsOrderDetailsOpen(false);
  };
  
  const handlePrintReceipt = (order: Order) => {
    // Implement receipt printing logic
  };
  
  // Filter and sort orders
  const filteredOrders = orders
    .filter(order => {
      // Status filter
      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false;
      }
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          order.orderNumber.toLowerCase().includes(search) ||
          order.id.toLowerCase().includes(search) ||
          (order.customerName && order.customerName.toLowerCase().includes(search))
        );
      }
      
      return true;
    })
    .sort((a, b) => b.createdAt - a.createdAt); // Sort by date, newest first
  
  // Pagination
  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  const getOrderTypeLabel = (type: OrderType) => {
    switch (type) {
      case OrderType.RETAIL:
        return 'Retail';
      case OrderType.DINE_IN:
        return 'Dine In';
      case OrderType.TAKEOUT:
        return 'Take Out';
      default:
        return type;
    }
  };
  
  const getOrderStatusChip = (status: OrderStatus) => {
    let color:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning';
    
    switch (status) {
      case OrderStatus.OPEN:
        color = 'info';
        break;
      case OrderStatus.COMPLETED:
        color = 'success';
        break;
      case OrderStatus.CANCELLED:
        color = 'error';
        break;
      case OrderStatus.REFUNDED:
        color = 'warning';
        break;
      case OrderStatus.PARTIALLY_REFUNDED:
        color = 'warning';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={status} color={color} size="small" />;
  };
  
  const getPaymentStatusChip = (status: PaymentStatus) => {
    let color:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'error'
      | 'info'
      | 'success'
      | 'warning';
    
    switch (status) {
      case PaymentStatus.PENDING:
        color = 'warning';
        break;
      case PaymentStatus.PAID:
        color = 'success';
        break;
      case PaymentStatus.FAILED:
        color = 'error';
        break;
      case PaymentStatus.REFUNDED:
        color = 'info';
        break;
      case PaymentStatus.PARTIALLY_REFUNDED:
        color = 'info';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={status} color={color} size="small" />;
  };
  
  const formatDate = (timestamp: number) => {
    return format(new Date(timestamp), 'MMM d, yyyy h:mm a');
  };
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        Order History
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Search orders..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2, flexGrow: 1 }}
          />
        </Box>
        
        <Tabs
          value={statusFilter}
          onChange={handleStatusFilterChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All Orders" value="all" />
          <Tab label="Open" value={OrderStatus.OPEN} />
          <Tab label="Completed" value={OrderStatus.COMPLETED} />
          <Tab label="Cancelled" value={OrderStatus.CANCELLED} />
          <Tab label="Refunded" value={OrderStatus.REFUNDED} />
        </Tabs>
      </Paper>
      
      <TableContainer component={Paper} sx={{ flexGrow: 1 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.map((order) => (
              <TableRow key={order.id} hover>
                <TableCell>{order.orderNumber}</TableCell>
                <TableCell>{formatDate(order.createdAt)}</TableCell>
                <TableCell>{getOrderTypeLabel(order.type)}</TableCell>
                <TableCell>{order.customerName || 'Guest'}</TableCell>
                <TableCell>${order.total.toFixed(2)}</TableCell>
                <TableCell>{getOrderStatusChip(order.status)}</TableCell>
                <TableCell>{getPaymentStatusChip(order.paymentStatus)}</TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    onClick={() => handleViewOrderDetails(order)}
                    title="View Details"
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handlePrintReceipt(order)}
                    title="Print Receipt"
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            
            {paginatedOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No orders found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      
      {/* Order Details Dialog */}
      <Dialog
        open={isOrderDetailsOpen}
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Order #{selectedOrder.orderNumber}
                </Typography>
                <Box>
                  {getOrderStatusChip(selectedOrder.status)}
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Order Information
                </Typography>
                <Grid>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Date</Typography>
                      <Typography variant="body1">{formatDate(selectedOrder.createdAt)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Order Type</Typography>
                      <Typography variant="body1">{getOrderTypeLabel(selectedOrder.type)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Customer</Typography>
                      <Typography variant="body1">{selectedOrder.customerName || 'Guest'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                      <Typography variant="body1">{getPaymentStatusChip(selectedOrder.paymentStatus)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Order Items
              </Typography>
              <List disablePadding>
                {selectedOrder.items.map((item) => (
                  item.id !== 'order-discount' && (
                    <ListItem key={item.id} sx={{ py: 1, px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">
                              {item.quantity} x {item.name}
                            </Typography>
                            <Typography variant="body1">
                              ${(item.price * item.quantity).toFixed(2)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          item.modifiers.length > 0 && (
                            <Box sx={{ ml: 2 }}>
                              {item.modifiers.map(mod => (
                                <Typography key={mod.id} variant="body2" color="text.secondary">
                                  {mod.name}: {mod.options.map(opt => opt.name).join(', ')}
                                </Typography>
                              ))}
                              {item.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  Note: {item.notes}
                                </Typography>
                              )}
                            </Box>
                          )
                        }
                      />
                    </ListItem>
                  )
                ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1">Subtotal</Typography>
                  <Typography variant="body1">${selectedOrder.subtotal.toFixed(2)}</Typography>
                </Box>
                
                {selectedOrder.discountAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1" color="error">Discount</Typography>
                    <Typography variant="body1" color="error">-${selectedOrder.discountAmount.toFixed(2)}</Typography>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1">Tax</Typography>
                  <Typography variant="body1">${selectedOrder.taxAmount.toFixed(2)}</Typography>
                </Box>
                
                {selectedOrder.tipAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">Tip</Typography>
                    <Typography variant="body1">${selectedOrder.tipAmount.toFixed(2)}</Typography>
                  </Box>
                )}
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6">${selectedOrder.total.toFixed(2)}</Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button 
                startIcon={<PrintIcon />}
                onClick={() => handlePrintReceipt(selectedOrder)}
              >
                Print Receipt
              </Button>
              <Button onClick={handleCloseOrderDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

const OrderHistory: React.FC = () => {
  return (
    <POSProvider>
      <POSLayout>
        <OrderHistoryContent />
      </POSLayout>
    </POSProvider>
  );
};

export default OrderHistory; 